import { INGREDIENT_CATALOG } from "./catalog";
import { OFFLINE_BOT_AVATAR_PATHS } from "@/lib/offline/botRoster";
import {
  MEM_ALICE_ID,
  MEM_BOB_ID,
  MEM_CPU_ID,
  offlineDevLoginPayload,
} from "./devAccounts";
import {
  BAZAAR_PACK_META,
  type BazaarPackKey,
} from "@/lib/trivia/bazaarOffers";
import { isCrownWheelSegment, isTriviaCategoryKey, TRIVIA_CATEGORY_KEYS } from "@/lib/trivia/catalog";
import type { TriviaMatchState } from "@/lib/trivia/matchTypes";
import { buildDebugFinishedTriviaState } from "@/lib/trivia/debugFinishedState";
import {
  applyAnswer,
  applyCrownCategoryPlacedQuestion,
  applyPowerUpFiftyToState,
  applyPowerUpTimeToState,
  applySkipToNewQuestionState,
  applySpinLandCrown,
  applySpinPlacedQuestion,
  computeSpinSegment,
  createInitialTriviaState,
  pickCategoryForCrownChoice,
  playerHasAllWedges,
  segmentToCategory,
  triviaDuelOutcomeForViewer,
} from "@/lib/trivia/engine";
import {
  duelResultsCoinsForOutcome,
  duelResultsXpForOutcome,
} from "@/lib/trivia/duelResultRewards";
import triviaQuestionsDocument from "@/lib/trivia/triviaQuestionsDocument";
import {
  buildLiveEventSnapshotPayload,
  synergyBonusPointsForAnswer,
} from "@/lib/events/liveEventSynergy";
import {
  npcCorrectChanceForMatch,
  npcEffectiveCorrectChanceForCpuAnswer,
} from "@/lib/trivia/npcMatchSkill";

function checkSignal(signal?: AbortSignal | null) {
  if (signal?.aborted) {
    const e = new Error("AbortError");
    e.name = "AbortError";
    throw e;
  }
}

function currentDailyPeriod(): string {
  const d = new Date();
  return `daily-${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

const QUEST_DEFS_DAILY = [
  {
    id: "daily_play_5",
    label: "Play 24 turns",
    target: 24,
    rewardCoins: 55,
    kind: "play_turns",
  },
  {
    id: "daily_balanced_3",
    label: "Answer 15 questions correctly",
    target: 15,
    rewardCoins: 60,
    kind: "balanced",
  },
  {
    id: "daily_new_combo_2",
    label: "Earn 9 new category wedges",
    target: 9,
    rewardCoins: 65,
    kind: "new_combo",
  },
] as const;

/** Offline mirror of `/events/live` — keep rules aligned with API seed (`live_events`). */
function offlineLiveEventsList(now: number) {
  return [
    {
      id: "offline-ev-world-tour",
      key: "dessert_week",
      rules: {
        label: "World Tour Week",
        blurb:
          "Maps and timelines score bigger — collect Geography & History wedges before the globe resets.",
        featuredCategories: ["geography", "history"],
        bonusSynergy: 4,
      },
      startsAt: new Date(now - 86400_000),
      endsAt: new Date(now + 30 * 86400_000),
    },
    {
      id: "offline-ev-arena",
      key: "spice_sprint",
      rules: {
        label: "Arena Spotlight",
        blurb:
          "Crowd-pleaser combos: rack up Entertainment & Sports wedges for bonus showdown points.",
        featuredCategories: ["entertainment", "sports"],
        bonusSynergy: 5,
      },
      startsAt: new Date(now - 2 * 86400_000),
      endsAt: new Date(now + 21 * 86400_000),
    },
    {
      id: "offline-ev-lab-night",
      key: "midnight_snack_rush",
      rules: {
        label: "Late Lab Session",
        blurb:
          "After-hours brain fuel — pair Science & Art wedges before the lights come back on.",
        featuredCategories: ["science", "art"],
        bonusSynergy: 3,
      },
      startsAt: new Date(now - 12 * 3600_000),
      endsAt: new Date(now + 14 * 86400_000),
    },
  ];
}

type IngRow = { id: string; key: string; name: string; tags: string[] };

type StoredMatch = {
  playersJson: string[];
  stateJson: TriviaMatchState;
  version: number;
  status: "active" | "finished";
  eventSnapshotJson: Record<string, unknown> | null;
  updatedAt: number;
  clientMoveIds: string[];
  /** When true, end-of-duel coin payout was applied (avoids double-grant on reload). */
  finishEconomyApplied?: boolean;
  /** Base coins/XP granted once at finish — used for idempotent 2× claim. */
  resultsPayoutByUserId?: Record<string, { coins: number; xp: number }>;
  results2xClaimedByUserId?: Record<string, true>;
};

const OFFLINE_PROFILE_AVATAR_STORAGE_KEY = "cwf-offline-profile-avatar-v1";
const ALLOWED_PROFILE_AVATAR = new Set<string>(
  OFFLINE_BOT_AVATAR_PATHS as readonly string[],
);

function normalizeOfflineProfileAvatar(
  raw: unknown,
): string | null | undefined {
  if (raw === undefined) return undefined;
  if (raw === null) return null;
  if (typeof raw !== "string") return undefined;
  const t = raw.trim();
  if (t === "") return null;
  return ALLOWED_PROFILE_AVATAR.has(t) ? t : undefined;
}

const blob = {
  ready: false,
  ingredients: [] as IngRow[],
  quests: new Map<
    string,
    { progress: number; completed: number; claimed: number }
  >(),
  dailyClaimed: new Set<string>(),
  matches: new Map<string, StoredMatch>(),
  currencies: {} as Record<
    string,
    {
      coins: number;
      gems: number;
      duelPowerBomb: number;
      duelPowerTime: number;
      duelPowerSkip: number;
    }
  >,
  xp: {} as Record<string, number>,
  chefLevel: {} as Record<string, number>,
  /** Chosen duel portrait (`/profile-bots/*.webp`), keyed by user id — persisted in localStorage. */
  profileAvatar: {} as Record<string, string>,
  /** Guest chefs only: display name override + setup completion — persisted in localStorage. */
  profileExt: {} as Record<
    string,
    { displayName?: string; setupComplete?: boolean }
  >,
  kitchenRecipeUnlocks: new Map<string, Set<string>>(),
  triviaQuestions: [] as Array<{
    id: string;
    packKey: string;
    categoryKey: string;
    prompt: string;
    choicesJson: [string, string, string, string];
    correctIndex: number;
    bankIndex?: number | null;
    heroImageUrl?: string | null;
  }>,
};

const OFFLINE_PROFILE_EXT_STORAGE_KEY = "cwf-offline-profile-ext-v1";

const OFFLINE_ECONOMY_STORAGE_KEY = "cwf-offline-economy-v1";

function hydrateOfflineEconomy(): void {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem(OFFLINE_ECONOMY_STORAGE_KEY);
    if (!raw) return;
    const o = JSON.parse(raw) as Record<string, unknown>;
    const cur = o.currencies;
    if (cur && typeof cur === "object") {
      for (const [k, v] of Object.entries(cur as Record<string, unknown>)) {
        if (!v || typeof v !== "object") continue;
        const row = v as Record<string, unknown>;
        const coins = row.coins;
        const gems = row.gems;
        if (typeof coins !== "number" || typeof gems !== "number") continue;
        blob.currencies[k] = {
          coins,
          gems,
          duelPowerBomb:
            typeof row.duelPowerBomb === "number" ? row.duelPowerBomb : 5,
          duelPowerTime:
            typeof row.duelPowerTime === "number" ? row.duelPowerTime : 5,
          duelPowerSkip:
            typeof row.duelPowerSkip === "number" ? row.duelPowerSkip : 10,
        };
      }
    }
    const xpObj = o.xp;
    if (xpObj && typeof xpObj === "object") {
      for (const [k, v] of Object.entries(xpObj as Record<string, unknown>)) {
        if (typeof v === "number" && Number.isFinite(v)) blob.xp[k] = v;
      }
    }
    const clObj = o.chefLevel;
    if (clObj && typeof clObj === "object") {
      for (const [k, v] of Object.entries(clObj as Record<string, unknown>)) {
        if (typeof v === "number" && Number.isFinite(v)) blob.chefLevel[k] = v;
      }
    }
    const qArr = o.questsSerialized;
    if (Array.isArray(qArr)) {
      for (const entry of qArr) {
        if (
          !entry ||
          typeof entry !== "object" ||
          !("k" in entry) ||
          !("v" in entry)
        )
          continue;
        const k = (entry as { k: string }).k;
        const v = (entry as { v: unknown }).v;
        if (typeof k !== "string" || !v || typeof v !== "object") continue;
        const r = v as Record<string, unknown>;
        const progress = r.progress;
        const completed = r.completed;
        const claimed = r.claimed;
        if (
          typeof progress !== "number" ||
          typeof completed !== "number" ||
          typeof claimed !== "number"
        )
          continue;
        blob.quests.set(k, { progress, completed, claimed });
      }
    }
    const daily = o.dailyClaimedSerialized;
    if (Array.isArray(daily)) {
      for (const k of daily) {
        if (typeof k === "string") blob.dailyClaimed.add(k);
      }
    }
    const kitchen = o.kitchenRecipeUnlocksSerialized;
    if (Array.isArray(kitchen)) {
      for (const row of kitchen) {
        if (!row || typeof row !== "object") continue;
        const uid = (row as { uid?: string }).uid;
        const keys = (row as { keys?: unknown }).keys;
        if (typeof uid !== "string" || !Array.isArray(keys)) continue;
        const s = new Set<string>();
        for (const rk of keys) {
          if (typeof rk === "string") s.add(rk);
        }
        blob.kitchenRecipeUnlocks.set(uid, s);
      }
    }
  } catch {
    /* ignore */
  }
}

function persistOfflineEconomy(): void {
  if (typeof window === "undefined") return;
  try {
    const questsSerialized = [...blob.quests.entries()].map(([k, v]) => ({
      k,
      v,
    }));
    const dailyClaimedSerialized = [...blob.dailyClaimed];
    const kitchenRecipeUnlocksSerialized = [
      ...blob.kitchenRecipeUnlocks.entries(),
    ].map(([uid, s]) => ({ uid, keys: [...s] }));
    localStorage.setItem(
      OFFLINE_ECONOMY_STORAGE_KEY,
      JSON.stringify({
        currencies: blob.currencies,
        xp: blob.xp,
        chefLevel: blob.chefLevel,
        questsSerialized,
        dailyClaimedSerialized,
        kitchenRecipeUnlocksSerialized,
      }),
    );
  } catch {
    /* quota / private mode */
  }
}

function hydrateOfflineProfileExt(): void {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem(OFFLINE_PROFILE_EXT_STORAGE_KEY);
    if (!raw) return;
    const o = JSON.parse(raw) as Record<string, unknown>;
    const uuidRe =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    for (const [k, v] of Object.entries(o)) {
      if (!uuidRe.test(k) || !v || typeof v !== "object") continue;
      const row = v as Record<string, unknown>;
      const ext: { displayName?: string; setupComplete?: boolean } = {};
      if (typeof row.displayName === "string") {
        const t = row.displayName.trim().replace(/\s+/g, " ");
        if (t.length >= 2) ext.displayName = t.slice(0, 24);
      }
      if (row.setupComplete === true) ext.setupComplete = true;
      blob.profileExt[k] = ext;
    }
  } catch {
    /* ignore */
  }
}

function persistOfflineProfileExt(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      OFFLINE_PROFILE_EXT_STORAGE_KEY,
      JSON.stringify(blob.profileExt),
    );
  } catch {
    /* quota / private mode */
  }
}

function hydrateOfflineProfileAvatars(): void {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem(OFFLINE_PROFILE_AVATAR_STORAGE_KEY);
    if (!raw) return;
    const o = JSON.parse(raw) as Record<string, unknown>;
    const uuidRe =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    for (const [k, v] of Object.entries(o)) {
      if (!uuidRe.test(k) || typeof v !== "string") continue;
      if (ALLOWED_PROFILE_AVATAR.has(v)) blob.profileAvatar[k] = v;
    }
  } catch {
    /* ignore */
  }
}

function persistOfflineProfileAvatars(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      OFFLINE_PROFILE_AVATAR_STORAGE_KEY,
      JSON.stringify(blob.profileAvatar),
    );
  } catch {
    /* quota / private mode */
  }
}

/** Survives refresh in dev offline mode (in-memory matches otherwise disappear). */
const OFFLINE_MATCHES_STORAGE_KEY = "cwf-offline-matches-v2";

function restoreOfflineMatches(): void {
  if (typeof window === "undefined") return;
  try {
    const raw = sessionStorage.getItem(OFFLINE_MATCHES_STORAGE_KEY);
    if (!raw) return;
    const rec = JSON.parse(raw) as Record<string, StoredMatch>;
    for (const [id, m] of Object.entries(rec)) {
      if (
        !m ||
        !Array.isArray(m.playersJson) ||
        !m.stateJson ||
        typeof m.version !== "number"
      ) {
        continue;
      }
      if (m.status === "finished" && m.finishEconomyApplied == null) {
        m.finishEconomyApplied = true;
      }
      blob.matches.set(id, m);
    }
  } catch {
    // ignore corrupt storage
  }
}

function persistOfflineMatches(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      OFFLINE_MATCHES_STORAGE_KEY,
      JSON.stringify(Object.fromEntries(blob.matches)),
    );
  } catch {
    // quota / private mode
  }
}

function questKey(uid: string, qid: string, period: string) {
  return `${uid}|${qid}|${period}`;
}

function packUnlockPriceCoins(rawKey: string): number | null {
  const k = rawKey.trim();
  if (!(k in BAZAAR_PACK_META)) return null;
  return BAZAAR_PACK_META[k as BazaarPackKey].priceCoins;
}

function ensureInit(): void {
  if (blob.ready) return;
  hydrateOfflineEconomy();
  blob.ready = true;
  blob.ingredients = INGREDIENT_CATALOG.map((c) => ({
    id: crypto.randomUUID(),
    key: c.key,
    name: c.name,
    tags: c.tags,
  }));
  blob.currencies[MEM_ALICE_ID] ??= {
    coins: 120,
    gems: 3,
    duelPowerBomb: 5,
    duelPowerTime: 5,
    duelPowerSkip: 10,
  };
  blob.currencies[MEM_BOB_ID] ??= {
    coins: 120,
    gems: 3,
    duelPowerBomb: 5,
    duelPowerTime: 5,
    duelPowerSkip: 10,
  };
  blob.xp[MEM_ALICE_ID] ??= 0;
  blob.xp[MEM_BOB_ID] ??= 0;
  blob.chefLevel[MEM_ALICE_ID] ??= 1;
  blob.chefLevel[MEM_BOB_ID] ??= 1;

  hydrateOfflineProfileAvatars();
  hydrateOfflineProfileExt();

  if (blob.triviaQuestions.length === 0) {
    for (const q of triviaQuestionsDocument.questions) {
      blob.triviaQuestions.push({
        id: q.id,
        packKey: q.packKey,
        categoryKey: q.categoryKey,
        prompt: q.prompt,
        choicesJson: q.choices as [string, string, string, string],
        correctIndex: q.correctIndex,
        bankIndex: q.bankIndex,
        heroImageUrl: q.heroImageUrl ?? null,
      });
    }
  }

  restoreOfflineMatches();
}

function requireUser(token: string | null | undefined): string {
  ensureInit();
  if (!token?.startsWith("cwf-offline-")) throw new Error("Unauthorized");
  const rest = token.slice("cwf-offline-".length);
  if (rest === "alice") return MEM_ALICE_ID;
  if (rest === "bob") return MEM_BOB_ID;
  const m =
    /^guest-([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i.exec(
      rest,
    );
  if (m) return m[1]!;
  throw new Error("Unauthorized");
}

function idMap(): Map<string, IngRow> {
  ensureInit();
  return new Map(blob.ingredients.map((r) => [r.id, r]));
}

function sanitizeTriviaState(
  viewerId: string,
  st: TriviaMatchState,
  players: string[],
): Record<string, unknown> {
  const activePid =
    players[st.currentPlayerIndex] ?? players[0] ?? "";
  const base: Record<string, unknown> = {
    phase: st.phase,
    wedges: st.wedges,
    scores: st.scores,
    currentPlayerIndex: st.currentPlayerIndex,
    playerIds: players,
    matchSeed: st.matchSeed,
    spinNonce: st.spinNonce,
    spinSegmentIndex: st.spinSegmentIndex,
    activeCategory: st.activeCategory,
    lastResult: st.lastResult,
    activeQuestionId: st.activeQuestionId,
    ...(st.answerCounts ? { answerCounts: st.answerCounts } : {}),
    ...(st.answerStreaks ? { answerStreaks: st.answerStreaks } : {}),
  };
  if (st.phase === "choose_category" && activePid === viewerId) {
    return { ...base, questionHidden: false, crownChoice: true };
  }
  if (st.phase === "choose_category") {
    return { ...base, questionHidden: true, crownChoice: false };
  }
  if (
    st.phase === "answer" &&
    st.activeQuestionId &&
    activePid === viewerId
  ) {
    return {
      ...base,
      questionHidden: false,
      ...(st.answerRound ? { answerRound: st.answerRound } : {}),
    };
  }
  if (st.phase === "answer") {
    return {
      ...base,
      questionHidden: true,
      activeQuestionId: null,
    };
  }
  return { ...base, questionHidden: false };
}

function offlineInferBankIndex(row: {
  id: string;
  bankIndex?: number | null;
}): number | null {
  if (row.bankIndex != null) return row.bankIndex;
  const m = /^tq-(\d+)$/.exec(row.id);
  return m ? parseInt(m[1]!, 10) : null;
}

function enrichWithQuestion(pub: Record<string, unknown>): Record<string, unknown> {
  const qid = pub.activeQuestionId as string | null | undefined;
  if (!qid || pub.questionHidden === true) return pub;
  ensureInit();
  const row = blob.triviaQuestions.find((q) => q.id === qid);
  if (!row) return pub;
  return {
    ...pub,
    activeQuestion: {
      id: row.id,
      categoryKey: row.categoryKey,
      prompt: row.prompt,
      choices: row.choicesJson,
      bankIndex: offlineInferBankIndex(row),
      heroImageUrl: row.heroImageUrl ?? null,
    },
  };
}

function pickRandomQuestion(
  categoryKey: string,
  excludeIds: string[],
): { id: string } | null {
  ensureInit();
  const pool = blob.triviaQuestions.filter(
    (q) => q.categoryKey === categoryKey && !excludeIds.includes(q.id),
  );
  if (pool.length === 0) return null;
  return { id: pool[Math.floor(Math.random() * pool.length)]!.id };
}

/** Auto-play CPU until human turn or finished. */
function resolveCpuTurns(m: StoredMatch, matchId: string): void {
  let safety = 0;
  /** One CPU answer per HTTP tick so the HUD can update between auto-plays. */
  let cpuAnswersThisResolve = 0;
  while (m.status === "active" && safety++ < 80) {
    const st0 = m.stateJson;
    const turnPid = st0.playerIds[st0.currentPlayerIndex] ?? st0.playerIds[0];
    if (turnPid !== MEM_CPU_ID) return;

    if (st0.phase === "spin") {
      const segment = computeSpinSegment(st0, matchId);
      if (isCrownWheelSegment(segment)) {
        const next = applySpinLandCrown({
          state: st0,
          segmentIndex: segment,
        });
        m.version += 1;
        m.stateJson = next;
        m.updatedAt = Date.now();
        m.clientMoveIds.push(crypto.randomUUID());
        continue;
      }
      const cat = segmentToCategory(segment);
      const picked = pickRandomQuestion(cat, st0.questionIdsUsed);
      if (!picked) throw new Error("CPU: no question");
      const next = applySpinPlacedQuestion({
        state: st0,
        category: cat,
        questionId: picked.id,
        segmentIndex: segment,
      });
      m.version += 1;
      m.stateJson = next;
      m.updatedAt = Date.now();
      m.clientMoveIds.push(crypto.randomUUID());
      continue;
    }

    if (st0.phase === "choose_category") {
      const cat = pickCategoryForCrownChoice(st0, MEM_CPU_ID, Math.random);
      const picked = pickRandomQuestion(cat, st0.questionIdsUsed);
      if (!picked) throw new Error("CPU: no question crown");
      const next = applyCrownCategoryPlacedQuestion({
        state: st0,
        category: cat,
        questionId: picked.id,
      });
      m.version += 1;
      m.stateJson = next;
      m.updatedAt = Date.now();
      m.clientMoveIds.push(crypto.randomUUID());
      continue;
    }

    if (st0.phase === "answer" && st0.activeQuestionId) {
      const qrow = blob.triviaQuestions.find(
        (x) => x.id === st0.activeQuestionId,
      );
      if (!qrow || !isTriviaCategoryKey(qrow.categoryKey)) return;
      const wrongIdx = [0, 1, 2, 3].filter((i) => i !== qrow.correctIndex);
      const humanId = st0.playerIds.find((p) => p !== MEM_CPU_ID) ?? "";
      const cpuCorrectChance = humanId
        ? npcEffectiveCorrectChanceForCpuAnswer(
            matchId,
            st0,
            humanId,
            MEM_CPU_ID,
          )
        : npcCorrectChanceForMatch(matchId);
      const pickCorrect = Math.random() < cpuCorrectChance;
      const choiceIndex = pickCorrect
        ? qrow.correctIndex
        : wrongIdx[Math.floor(Math.random() * wrongIdx.length)]!;
      const correct = choiceIndex === qrow.correctIndex;
      const wedgesBefore = new Set(st0.wedges[MEM_CPU_ID] ?? []);
      const next = applyAnswer({
        state: st0,
        playerId: MEM_CPU_ID,
        correct,
        category: qrow.categoryKey,
      });
      cpuAnswersThisResolve += 1;
      const wedgesAfter = new Set(next.wedges[MEM_CPU_ID] ?? []);
      if (correct) {
        const syn = synergyBonusPointsForAnswer(
          m.eventSnapshotJson,
          wedgesBefore,
          wedgesAfter,
        );
        if (syn > 0) {
          next.scores[MEM_CPU_ID] =
            (next.scores[MEM_CPU_ID] ?? 0) + syn;
        }
      }
      const won = playerHasAllWedges(next.wedges, MEM_CPU_ID);
      m.version += 1;
      m.stateJson = next;
      m.status = won ? "finished" : "active";
      m.updatedAt = Date.now();
      m.clientMoveIds.push(crypto.randomUUID());
      if (m.clientMoveIds.length > 120) {
        m.clientMoveIds.splice(0, m.clientMoveIds.length - 40);
      }
      if (won) {
        applyOfflineDuelFinishEconomyOnce(matchId, m);
        return;
      }
      if (cpuAnswersThisResolve >= 1) return;
      continue;
    }
    return;
  }
}

function applyOfflineDuelFinishEconomyOnce(_matchId: string, m: StoredMatch) {
  if (m.status !== "finished" || m.finishEconomyApplied) return;
  const st = m.stateJson;
  const players = m.playersJson;
  const payout: Record<string, { coins: number; xp: number }> = {};
  for (const myId of players) {
    if (myId === MEM_CPU_ID) continue;
    const rivalId = players.find((p) => p !== myId);
    if (!rivalId) continue;
    const outcome = triviaDuelOutcomeForViewer({
      wedges: st.wedges,
      scores: st.scores,
      myId,
      rivalId,
    });
    const myScore = st.scores[myId] ?? 0;
    const coins = duelResultsCoinsForOutcome(outcome, myScore);
    const xp = duelResultsXpForOutcome(outcome, myScore);
    blob.currencies[myId] ??= {
      coins: 0,
      gems: 0,
      duelPowerBomb: 5,
      duelPowerTime: 5,
      duelPowerSkip: 10,
    };
    blob.currencies[myId].coins += coins;
    awardXp(myId, xp);
    payout[myId] = { coins, xp };
  }
  m.resultsPayoutByUserId = payout;
  m.finishEconomyApplied = true;
  persistOfflineEconomy();
}

function bumpQuestProgress(
  userId: string,
  ctx: {
    playedTurn: boolean;
    correctAnswer: boolean;
    newWedge: boolean;
  },
) {
  const period = currentDailyPeriod();

  const bumpOne = (qid: string, n: number) => {
    const k = questKey(userId, qid, period);
    const row = blob.quests.get(k);
    if (!row) blob.quests.set(k, { progress: n, completed: 0, claimed: 0 });
    else row.progress += n;
  };

  if (ctx.playedTurn) bumpOne("daily_play_5", 1);
  if (ctx.correctAnswer) bumpOne("daily_balanced_3", 1);
  if (ctx.newWedge) bumpOne("daily_new_combo_2", 1);

  for (const q of QUEST_DEFS_DAILY) {
    const row = blob.quests.get(questKey(userId, q.id, period));
    if (!row) continue;
    if (row.progress < q.target && row.completed === 1 && row.claimed === 0) {
      row.completed = 0;
    }
    if (row.progress >= q.target && row.completed === 0) row.completed = 1;
  }
}

function awardXp(uid: string, delta: number) {
  ensureInit();
  const nx = (blob.xp[uid] ?? 0) + delta;
  blob.xp[uid] = nx;
  blob.chefLevel[uid] = Math.min(20, Math.floor(nx / 120) + 1);
}

export async function offlineApiJson<T>(
  path: string,
  opts?: RequestInit & { token?: string | null },
): Promise<T> {
  await Promise.resolve();
  ensureInit();
  checkSignal(opts?.signal);
  const method = opts?.method ?? "GET";

  let body: Record<string, unknown> = {};
  if (opts?.body && typeof opts.body === "string") {
    try {
      body = JSON.parse(opts.body) as Record<string, unknown>;
    } catch {
      body = {};
    }
  }

  const cleanPath = path.split("?")[0] ?? "";

  const uuidRe =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  if (cleanPath === "/ingredients" && method === "GET") {
    return blob.ingredients.map((r) => ({
      id: r.id,
      key: r.key,
      name: r.name,
      tags: [...r.tags],
    })) as T;
  }

  if (cleanPath === "/dev/roster" && method === "GET") {
    return [
      {
        id: MEM_ALICE_ID,
        slug: "alice",
        displayName: "Alice",
      },
      {
        id: MEM_BOB_ID,
        slug: "bob",
        displayName: "Bob",
      },
    ] as T;
  }

  if (cleanPath === "/dev/login" && method === "POST") {
    if (body.guest === true) return offlineDevLoginPayload("alice") as T;
    const slugRaw = body.slug as string | undefined;
    if (slugRaw === "alice" || slugRaw === "bob") {
      return offlineDevLoginPayload(slugRaw) as T;
    }
    const u =
      typeof body.username === "string" ? body.username.trim().toLowerCase() : "";
    const pwd = body.password as string | undefined;
    if ((u === "alice" || u === "bob") && typeof pwd === "string" && pwd.length > 0) {
      return offlineDevLoginPayload(u) as T;
    }
    throw new Error(
      "login: guest, slug alice|bob, or username+password for dev chefs",
    );
  }

  const debugFinishPath =
    /^\/dev\/matches\/([^/]+)\/debug-finish$/.exec(cleanPath);
  if (debugFinishPath && method === "POST") {
    const id = decodeURIComponent(debugFinishPath[1]);
    const fromBody = body.outcome;
    const fromEnv =
      import.meta.env.NEXT_PUBLIC_CWF_DEBUG_MATCH_OUTCOME?.trim().toLowerCase();
    const outcome =
      fromBody === "win" || fromBody === "lose"
        ? fromBody
        : fromEnv === "win" || fromEnv === "lose"
          ? fromEnv
          : null;
    if (!outcome) {
      throw new Error(
        'offline debug-finish: send {"outcome":"win"|"lose"} or set NEXT_PUBLIC_CWF_DEBUG_MATCH_OUTCOME in .env.local',
      );
    }
    const uid = requireUser(opts?.token);
    const m = blob.matches.get(id);
    if (!m || !m.playersJson.includes(uid)) throw new Error("Match not found");
    let next: TriviaMatchState;
    try {
      next = buildDebugFinishedTriviaState(m.stateJson, uid, outcome);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Bad state";
      throw new Error(msg);
    }
    m.stateJson = next;
    m.status = "finished";
    m.version += 1;
    m.updatedAt = Date.now();
    if (!m.finishEconomyApplied) {
      applyOfflineDuelFinishEconomyOnce(id, m);
    }
    persistOfflineMatches();
    const rivalId = m.playersJson.find((p) => p !== uid);
    const winnerId = outcome === "win" ? uid : rivalId ?? uid;
    return {
      version: m.version,
      status: "finished",
      winnerId,
    } as T;
  }

  const debugSetQuestionPath =
    /^\/dev\/matches\/([^/]+)\/debug-set-question$/.exec(cleanPath);
  if (debugSetQuestionPath && method === "POST") {
    const id = decodeURIComponent(debugSetQuestionPath[1]);
    const bankRaw = body.bankIndex;
    const bankIndex =
      typeof bankRaw === "number" && Number.isInteger(bankRaw)
        ? bankRaw
        : typeof bankRaw === "string"
          ? Number.parseInt(bankRaw, 10)
          : Number.NaN;
    if (!Number.isFinite(bankIndex) || bankIndex < 0) {
      throw new Error('offline debug-set-question: send {"bankIndex": number}');
    }
    const uid = requireUser(opts?.token);
    const m = blob.matches.get(id);
    if (!m || m.status !== "active" || !m.playersJson.includes(uid)) {
      throw new Error("Active match not found");
    }
    const row =
      blob.triviaQuestions.find((q) => q.bankIndex === bankIndex) ??
      blob.triviaQuestions.find((q) => q.id === `tq-${bankIndex}`);
    if (!row) throw new Error("No trivia question for that bank index");
    if (!isTriviaCategoryKey(row.categoryKey)) {
      throw new Error("Invalid category on trivia row");
    }

    const s0 = m.stateJson;
    const viewerIdx = s0.playerIds.indexOf(uid);
    if (viewerIdx < 0) throw new Error("Viewer not in engine roster");

    const next: TriviaMatchState = structuredClone(s0);
    next.phase = "answer";
    next.currentPlayerIndex = viewerIdx;
    next.activeCategory = row.categoryKey;
    next.activeQuestionId = row.id;
    if (!next.questionIdsUsed.includes(row.id)) {
      next.questionIdsUsed = [...next.questionIdsUsed, row.id];
    }
    const seg = TRIVIA_CATEGORY_KEYS.indexOf(row.categoryKey);
    if (seg < 0) throw new Error("Category not on wheel");
    next.spinSegmentIndex = seg;
    next.lastResult = null;
    delete next.answerRound;

    m.stateJson = next;
    m.version += 1;
    m.updatedAt = Date.now();
    persistOfflineMatches();

    let pub = sanitizeTriviaState(uid, next, m.playersJson);
    pub = enrichWithQuestion(pub);
    return {
      version: m.version,
      status: "active",
      state: pub,
    } as T;
  }

  const uid = requireUser(opts?.token);

  if (cleanPath === "/me" && method === "GET") {
    const isDevChef = uid === MEM_ALICE_ID || uid === MEM_BOB_ID;
    const devChefDefaultName =
      uid === MEM_ALICE_ID ? "Alice" : uid === MEM_BOB_ID ? "Bob" : null;
    const storedName = blob.profileExt[uid]?.displayName?.trim();
    const displayName =
      storedName || (devChefDefaultName ?? "Chef");
    const profileSetupComplete = isDevChef
      ? true
      : blob.profileExt[uid]?.setupComplete === true;

    if (!isDevChef) {
      const hadWallet = Object.prototype.hasOwnProperty.call(
        blob.currencies,
        uid,
      );
      blob.currencies[uid] ??= {
        coins: 120,
        gems: 3,
        duelPowerBomb: 5,
        duelPowerTime: 5,
        duelPowerSkip: 10,
      };
      blob.xp[uid] ??= 0;
      blob.chefLevel[uid] ??= 1;
      if (!hadWallet) persistOfflineEconomy();
    }

    const c = blob.currencies[uid] ?? {
      coins: 0,
      gems: 0,
      duelPowerBomb: 5,
      duelPowerTime: 5,
      duelPowerSkip: 10,
    };
    const unlockSet = blob.kitchenRecipeUnlocks.get(uid);
    const kitchenRecipeUnlocks = [...(unlockSet ?? new Set<string>())].sort(
      (a, b) => a.localeCompare(b),
    );
    const avatarUrl = blob.profileAvatar[uid] ?? null;
    return {
      id: uid,
      displayName,
      avatarUrl,
      profileSetupComplete,
      chefLevel: blob.chefLevel[uid] ?? 1,
      xp: blob.xp[uid] ?? 0,
      coins: c.coins,
      gems: c.gems,
      duelPowerBomb: c.duelPowerBomb ?? 5,
      duelPowerTime: c.duelPowerTime ?? 5,
      duelPowerSkip: c.duelPowerSkip ?? 10,
      kitchenRecipeUnlocks,
    } as T;
  }

  if (cleanPath === "/me" && method === "PATCH") {
    const hasDisplay = Object.prototype.hasOwnProperty.call(body, "displayName");
    const hasAvatar = Object.prototype.hasOwnProperty.call(body, "avatarUrl");
    const hasDone = Object.prototype.hasOwnProperty.call(
      body,
      "profileSetupComplete",
    );
    if (!hasDisplay && !hasAvatar && !hasDone) {
      throw new Error("Nothing to update");
    }

    const isDevChef = uid === MEM_ALICE_ID || uid === MEM_BOB_ID;
    const devChefDefaultName =
      uid === MEM_ALICE_ID ? "Alice" : uid === MEM_BOB_ID ? "Bob" : null;

    const ext = { ...(blob.profileExt[uid] ?? {}) };
    if (hasDisplay) {
      const n =
        typeof body.displayName === "string"
          ? body.displayName.trim().replace(/\s+/g, " ")
          : "";
      if (n.length < 2 || n.length > 24) throw new Error("Invalid displayName");
      ext.displayName = n.slice(0, 24);
    }
    if (hasDone) {
      if (body.profileSetupComplete !== true) {
        throw new Error("profileSetupComplete must be true");
      }
      const mergedName =
        ext.displayName ??
        blob.profileExt[uid]?.displayName ??
        devChefDefaultName ??
        "";
      const t = mergedName.trim();
      if (t.length < 2) {
        throw new Error("displayName is required when completing profile setup");
      }
      ext.displayName = t.slice(0, 24);
      ext.setupComplete = true;
    }
    if (hasDisplay || hasDone) {
      blob.profileExt[uid] = ext;
      persistOfflineProfileExt();
    }
    if (hasAvatar) {
      const next = normalizeOfflineProfileAvatar(body.avatarUrl);
      if (next === undefined) throw new Error("Invalid avatarUrl");
      if (next === null) delete blob.profileAvatar[uid];
      else blob.profileAvatar[uid] = next;
      persistOfflineProfileAvatars();
    }

    const storedNameOut = blob.profileExt[uid]?.displayName?.trim();
    const displayNameOut =
      storedNameOut || (devChefDefaultName ?? "Chef");
    const profileSetupCompleteOut = isDevChef
      ? true
      : blob.profileExt[uid]?.setupComplete === true;

    const c = blob.currencies[uid] ?? {
      coins: 0,
      gems: 0,
      duelPowerBomb: 5,
      duelPowerTime: 5,
      duelPowerSkip: 10,
    };
    const unlockSet = blob.kitchenRecipeUnlocks.get(uid);
    const kitchenRecipeUnlocks = [...(unlockSet ?? new Set<string>())].sort(
      (a, b) => a.localeCompare(b),
    );
    const avatarUrl = blob.profileAvatar[uid] ?? null;
    return {
      id: uid,
      displayName: displayNameOut,
      avatarUrl,
      profileSetupComplete: profileSetupCompleteOut,
      chefLevel: blob.chefLevel[uid] ?? 1,
      xp: blob.xp[uid] ?? 0,
      coins: c.coins,
      gems: c.gems,
      duelPowerBomb: c.duelPowerBomb ?? 5,
      duelPowerTime: c.duelPowerTime ?? 5,
      duelPowerSkip: c.duelPowerSkip ?? 10,
      kitchenRecipeUnlocks,
    } as T;
  }
  if (cleanPath === "/daily-reward/claim" && method === "POST") {
    const day = `${new Date().getUTCFullYear()}-${String(new Date().getUTCMonth() + 1).padStart(2, "0")}-${String(new Date().getUTCDate()).padStart(2, "0")}`;
    const k = `${uid}|${day}`;
    if (blob.dailyClaimed.has(k)) return { alreadyClaimed: true, coins: 0 } as T;
    blob.dailyClaimed.add(k);
    const reward = 25 + Math.floor(Math.random() * 15);
    blob.currencies[uid] ??= {
      coins: 0,
      gems: 0,
      duelPowerBomb: 5,
      duelPowerTime: 5,
      duelPowerSkip: 10,
    };
    blob.currencies[uid].coins += reward;
    persistOfflineEconomy();
    return { claimed: true, coins: reward } as T;
  }

  /** Prize shop rewarded-video grants (must match `COIN_OFFERS` / `GEM_OFFERS` / `ENERGY_OFFERS` ids). */
  if (cleanPath === "/store/rewarded-claim" && method === "POST") {
    const offerIdRaw = body.offerId;
    const offerId =
      typeof offerIdRaw === "string" ? offerIdRaw.trim() : "";
    const grants: Record<
      string,
      { coins?: number; gems?: number; energy?: number }
    > = {
      "c-ad": { coins: 200 },
      "g-ad": { gems: 12 },
      "e-ad": { energy: 8 },
    };
    const grant = grants[offerId];
    if (!grant) throw new Error("Unknown rewarded offer");

    if (grant.energy != null && grant.energy > 0) {
      return {
        ok: true as const,
        offerId,
        energy: grant.energy,
      } as T;
    }

    blob.currencies[uid] ??= {
      coins: 0,
      gems: 0,
      duelPowerBomb: 5,
      duelPowerTime: 5,
      duelPowerSkip: 10,
    };

    if (grant.coins != null && grant.coins > 0) {
      blob.currencies[uid].coins += grant.coins;
      persistOfflineEconomy();
      return { ok: true as const, offerId, coins: grant.coins } as T;
    }
    if (grant.gems != null && grant.gems > 0) {
      blob.currencies[uid].gems += grant.gems;
      persistOfflineEconomy();
      return { ok: true as const, offerId, gems: grant.gems } as T;
    }

    throw new Error("Invalid rewarded grant");
  }

  if (cleanPath === "/quests" && method === "GET") {
    const period = currentDailyPeriod();
    for (const q of QUEST_DEFS_DAILY) {
      const row = blob.quests.get(questKey(uid, q.id, period));
      if (
        row &&
        row.progress < q.target &&
        row.completed === 1 &&
        row.claimed === 0
      ) {
        row.completed = 0;
      }
    }
    const res = QUEST_DEFS_DAILY.map((q) => {
      const row = blob.quests.get(questKey(uid, q.id, period));
      return {
        ...q,
        progress: row?.progress ?? 0,
        completed: (row?.completed ?? 0) === 1,
        claimed: (row?.claimed ?? 0) === 1,
      };
    });
    return { period, quests: [...res] } as T;
  }

  const questClaim = /^\/quests\/([^/]+)\/claim$/.exec(cleanPath);
  if (questClaim && method === "POST") {
    const questId = decodeURIComponent(questClaim[1]);
    const def = QUEST_DEFS_DAILY.find((x) => x.id === questId);
    if (!def) throw new Error("Unknown quest");
    const period = currentDailyPeriod();
    const row = blob.quests.get(questKey(uid, questId, period));
    if (!row || row.progress < def.target || row.completed !== 1)
      throw new Error("Quest not complete");
    if (row.claimed === 1) return { alreadyClaimed: true } as T;
    row.claimed = 1;
    blob.currencies[uid] ??= {
      coins: 0,
      gems: 0,
      duelPowerBomb: 5,
      duelPowerTime: 5,
      duelPowerSkip: 10,
    };
    blob.currencies[uid].coins += def.rewardCoins;
    persistOfflineEconomy();
    return { ok: true, rewardCoins: def.rewardCoins } as T;
  }

  if (cleanPath === "/shop/kitchen-recipe/unlock" && method === "POST") {
    const key = body.recipeKey;
    if (typeof key !== "string" || !key.trim()) {
      throw new Error("unlock: recipeKey required");
    }
    const recipeKey = key.trim();
    const price = packUnlockPriceCoins(recipeKey);
    if (price == null) throw new Error("Recipe not for sale");

    let s = blob.kitchenRecipeUnlocks.get(uid);
    if (!s) {
      s = new Set<string>();
      blob.kitchenRecipeUnlocks.set(uid, s);
    }
    if (s.has(recipeKey)) {
      throw new Error("already_owned");
    }

    blob.currencies[uid] ??= {
      coins: 0,
      gems: 0,
      duelPowerBomb: 5,
      duelPowerTime: 5,
      duelPowerSkip: 10,
    };
    const cur = blob.currencies[uid];
    if (cur.coins < price) {
      throw new Error("insufficient");
    }

    cur.coins -= price;
    s.add(recipeKey);
    persistOfflineEconomy();
    const kitchenRecipeUnlocks = [...s].sort((a, b) => a.localeCompare(b));
    return {
      ok: true,
      recipeKey,
      coins: cur.coins,
      gems: cur.gems,
      kitchenRecipeUnlocks,
    } as T;
  }

  if (
    (cleanPath === "/events/live" || cleanPath === "/events/active") &&
    method === "GET"
  ) {
    const now = Date.now();
    const events = offlineLiveEventsList(now);
    if (cleanPath === "/events/live") {
      return { events } as T;
    }
    return { active: events[0] ?? null } as T;
  }

  if (cleanPath === "/matches" && method === "GET") {
    const ordered = [...blob.matches.entries()].sort(
      (a, b) => b[1].updatedAt - a[1].updatedAt,
    );
    return ordered.map(([id, m]) => {
      const st = m.stateJson;
      const turnPid =
        st.playerIds[
          st.currentPlayerIndex >= 0 ? st.currentPlayerIndex : 0
        ] ?? st.playerIds[0];
      return {
        id,
        status: m.status,
        version: m.version,
        players: m.playersJson,
        yourTurn: turnPid === uid,
        turnPlayerId: turnPid,
        scores: st.scores,
        phase: st.phase,
        wedges: st.wedges,
        updatedAt: new Date(m.updatedAt),
        ...sanitizeTriviaState(uid, st, m.playersJson),
      };
    }) as T;
  }

  if (cleanPath === "/matches" && method === "POST") {
    const opp = body.opponentUserId as string | undefined;
    if (!opp || !uuidRe.test(opp)) throw new Error("Invalid opponent");
    ensureInit();
    if (blob.triviaQuestions.length < 6) throw new Error("Catalog too small");
    const players = [uid, opp].sort();
    const eventSnap = buildLiveEventSnapshotPayload(
      offlineLiveEventsList(Date.now()).map((e) => ({
        key: e.key,
        rules: e.rules,
      })),
    );
    const st = createInitialTriviaState(players, crypto.randomUUID(), {
      firstTurnPlayerId: uid,
    });
    const mid = crypto.randomUUID();
    blob.matches.set(mid, {
      playersJson: players,
      stateJson: st,
      version: 0,
      status: "active",
      eventSnapshotJson: eventSnap,
      updatedAt: Date.now(),
      clientMoveIds: [],
    });
    persistOfflineMatches();
    return { matchId: mid } as T;
  }

  const matchDetail = /^\/matches\/([^/]+)$/.exec(cleanPath);
  if (matchDetail && method === "GET") {
    const id = decodeURIComponent(matchDetail[1]);
    const m = blob.matches.get(id);
    if (!m || !m.playersJson.includes(uid)) throw new Error("Match not found");
    resolveCpuTurns(m, id);
    persistOfflineMatches();
    const st = m.stateJson;
    const turnPid = st.playerIds[st.currentPlayerIndex] ?? st.playerIds[0];
    let pub = sanitizeTriviaState(uid, st, m.playersJson);
    pub = enrichWithQuestion(pub);
    const playerAvatars: Record<string, string | null> = {};
    for (const pid of m.playersJson) {
      playerAvatars[pid] = blob.profileAvatar[pid] ?? null;
    }
    return {
      id,
      status: m.status,
      version: m.version,
      players: m.playersJson,
      yourTurn: turnPid === uid,
      turnPlayerId: turnPid,
      scores: st.scores,
      ...pub,
      eventModifiers: m.eventSnapshotJson,
      playerAvatars,
    } as T;
  }

  const spinP = /^\/matches\/([^/]+)\/moves\/spin$/.exec(cleanPath);
  if (spinP && method === "POST") {
    const id = decodeURIComponent(spinP[1]);
    const m = blob.matches.get(id);
    if (!m || !m.playersJson.includes(uid)) throw new Error("Match not found");
    const clientMoveId = body.clientMoveId as string | undefined;
    if (!clientMoveId || !uuidRe.test(clientMoveId)) throw new Error("Bad request");
    const st0 = structuredClone(m.stateJson) as TriviaMatchState;
    const turnPid = st0.playerIds[st0.currentPlayerIndex] ?? st0.playerIds[0];
    if (turnPid !== uid || m.status !== "active") {
      throw new Error(turnPid !== uid ? "Not your turn" : "Match finished");
    }
    if (st0.phase !== "spin") throw new Error("Not spin phase");
    const recentTail = m.clientMoveIds.slice(-40);
    if (recentTail.includes(clientMoveId)) {
      let pub = sanitizeTriviaState(uid, m.stateJson, m.playersJson);
      pub = enrichWithQuestion(pub);
      return {
        idempotent: true,
        version: m.version,
        state: pub,
      } as T;
    }
    const segment = computeSpinSegment(st0, id);
    if (isCrownWheelSegment(segment)) {
      const next = applySpinLandCrown({
        state: st0,
        segmentIndex: segment,
      });
      m.version += 1;
      m.stateJson = next;
      m.updatedAt = Date.now();
      m.clientMoveIds.push(clientMoveId);
      if (m.clientMoveIds.length > 120) {
        m.clientMoveIds.splice(0, m.clientMoveIds.length - 40);
      }
      resolveCpuTurns(m, id);
      persistOfflineMatches();
      let pub = sanitizeTriviaState(uid, m.stateJson, m.playersJson);
      pub = enrichWithQuestion(pub);
      return {
        version: m.version,
        status: m.status,
        segment,
        isCrown: true,
        state: pub,
      } as T;
    }
    const cat = segmentToCategory(segment);
    const picked = pickRandomQuestion(cat, st0.questionIdsUsed);
    if (!picked) throw new Error("No question available");
    const next = applySpinPlacedQuestion({
      state: st0,
      category: cat,
      questionId: picked.id,
      segmentIndex: segment,
    });
    m.version += 1;
    m.stateJson = next;
    m.updatedAt = Date.now();
    m.clientMoveIds.push(clientMoveId);
    if (m.clientMoveIds.length > 120) {
      m.clientMoveIds.splice(0, m.clientMoveIds.length - 40);
    }
    resolveCpuTurns(m, id);
    persistOfflineMatches();
    let pub = sanitizeTriviaState(uid, m.stateJson, m.playersJson);
    pub = enrichWithQuestion(pub);
    return {
      version: m.version,
      status: m.status,
      segment,
      category: cat,
      questionId: picked.id,
      state: pub,
    } as T;
  }

  const pickCatP = /^\/matches\/([^/]+)\/moves\/pick-category$/.exec(cleanPath);
  if (pickCatP && method === "POST") {
    const id = decodeURIComponent(pickCatP[1]);
    const m = blob.matches.get(id);
    if (!m || !m.playersJson.includes(uid)) throw new Error("Match not found");
    const clientMoveId = body.clientMoveId as string | undefined;
    const rawCat = body.categoryKey as string | undefined;
    if (!clientMoveId || !uuidRe.test(clientMoveId)) throw new Error("Bad request");
    if (!rawCat || !isTriviaCategoryKey(rawCat)) throw new Error("Bad category");
    const st0 = structuredClone(m.stateJson) as TriviaMatchState;
    const turnPid = st0.playerIds[st0.currentPlayerIndex] ?? st0.playerIds[0];
    if (turnPid !== uid || m.status !== "active") {
      throw new Error(turnPid !== uid ? "Not your turn" : "Match finished");
    }
    if (st0.phase !== "choose_category") throw new Error("Not choosing category");
    const recentTail = m.clientMoveIds.slice(-40);
    if (recentTail.includes(clientMoveId)) {
      let pub = sanitizeTriviaState(uid, m.stateJson, m.playersJson);
      pub = enrichWithQuestion(pub);
      return {
        idempotent: true,
        version: m.version,
        state: pub,
      } as T;
    }
    const picked = pickRandomQuestion(rawCat, st0.questionIdsUsed);
    if (!picked) throw new Error("No question available");
    const next = applyCrownCategoryPlacedQuestion({
      state: st0,
      category: rawCat,
      questionId: picked.id,
    });
    m.version += 1;
    m.stateJson = next;
    m.updatedAt = Date.now();
    m.clientMoveIds.push(clientMoveId);
    if (m.clientMoveIds.length > 120) {
      m.clientMoveIds.splice(0, m.clientMoveIds.length - 40);
    }
    resolveCpuTurns(m, id);
    persistOfflineMatches();
    let pub = sanitizeTriviaState(uid, m.stateJson, m.playersJson);
    pub = enrichWithQuestion(pub);
    return {
      version: m.version,
      status: m.status,
      category: rawCat,
      questionId: picked.id,
      state: pub,
    } as T;
  }

  const powerUpP = /^\/matches\/([^/]+)\/moves\/power-up$/.exec(cleanPath);
  if (powerUpP && method === "POST") {
    const id = decodeURIComponent(powerUpP[1]);
    const m = blob.matches.get(id);
    if (!m || !m.playersJson.includes(uid)) throw new Error("Match not found");
    const clientMoveId = body.clientMoveId as string | undefined;
    const rawKind = body.kind as string | undefined;
    if (!clientMoveId || !uuidRe.test(clientMoveId)) throw new Error("Bad request");
    const kind =
      rawKind === "fifty" || rawKind === "time" || rawKind === "skip"
        ? rawKind
        : null;
    if (!kind) throw new Error("Bad request");

    const st0 = structuredClone(m.stateJson) as TriviaMatchState;
    const turnPid = st0.playerIds[st0.currentPlayerIndex] ?? st0.playerIds[0];
    if (turnPid !== uid || m.status !== "active") {
      throw new Error(turnPid !== uid ? "Not your turn" : "Match finished");
    }
    if (st0.phase !== "answer" || !st0.activeQuestionId) {
      throw new Error("No active question");
    }

    const recentTail = m.clientMoveIds.slice(-40);
    if (recentTail.includes(clientMoveId)) {
      let pub = sanitizeTriviaState(uid, m.stateJson, m.playersJson);
      pub = enrichWithQuestion(pub);
      const c = blob.currencies[uid] ?? {
        coins: 0,
        gems: 0,
        duelPowerBomb: 5,
        duelPowerTime: 5,
        duelPowerSkip: 10,
      };
      return {
        idempotent: true,
        version: m.version,
        state: pub,
        duelPowers: {
          duelPowerBomb: c.duelPowerBomb ?? 5,
          duelPowerTime: c.duelPowerTime ?? 5,
          duelPowerSkip: c.duelPowerSkip ?? 10,
        },
      } as T;
    }

    blob.currencies[uid] ??= {
      coins: 0,
      gems: 0,
      duelPowerBomb: 5,
      duelPowerTime: 5,
      duelPowerSkip: 10,
    };
    const cur = blob.currencies[uid];
    const needCol =
      kind === "fifty"
        ? "duelPowerBomb"
        : kind === "time"
          ? "duelPowerTime"
          : "duelPowerSkip";
    if ((cur[needCol] ?? 0) < 1) throw new Error("No power-ups left");

    const qrow = blob.triviaQuestions.find((x) => x.id === st0.activeQuestionId);
    if (!qrow || !isTriviaCategoryKey(qrow.categoryKey)) {
      throw new Error("Bad question");
    }

    let nextState: TriviaMatchState;
    if (kind === "fifty") {
      const n = applyPowerUpFiftyToState(st0, qrow.correctIndex);
      if (!n) throw new Error("50/50 already used");
      nextState = n;
    } else if (kind === "time") {
      const n = applyPowerUpTimeToState(st0);
      if (!n) throw new Error("Time boost already used");
      nextState = n;
    } else {
      const picked = pickRandomQuestion(qrow.categoryKey, st0.questionIdsUsed);
      if (!picked) throw new Error("No alternate question");
      const n = applySkipToNewQuestionState(st0, picked.id);
      if (!n) throw new Error("Skip failed");
      nextState = n;
    }

    cur[needCol] -= 1;

    m.version += 1;
    m.stateJson = nextState;
    m.updatedAt = Date.now();
    m.clientMoveIds.push(clientMoveId);
    if (m.clientMoveIds.length > 120) {
      m.clientMoveIds.splice(0, m.clientMoveIds.length - 40);
    }
    resolveCpuTurns(m, id);
    persistOfflineEconomy();
    persistOfflineMatches();
    let pub = sanitizeTriviaState(uid, m.stateJson, m.playersJson);
    pub = enrichWithQuestion(pub);
    return {
      version: m.version,
      state: pub,
      duelPowers: {
        duelPowerBomb: cur.duelPowerBomb,
        duelPowerTime: cur.duelPowerTime,
        duelPowerSkip: cur.duelPowerSkip,
      },
    } as T;
  }

  const answerP = /^\/matches\/([^/]+)\/moves\/answer$/.exec(cleanPath);
  if (answerP && method === "POST") {
    const id = decodeURIComponent(answerP[1]);
    const m = blob.matches.get(id);
    if (!m || !m.playersJson.includes(uid)) throw new Error("Match not found");
    const choiceIndex = body.choiceIndex;
    const clientMoveId = body.clientMoveId as string | undefined;
    if (
      typeof choiceIndex !== "number" ||
      choiceIndex < 0 ||
      choiceIndex > 3 ||
      !clientMoveId ||
      !uuidRe.test(clientMoveId)
    ) {
      throw new Error("Bad request");
    }
    const st0 = structuredClone(m.stateJson) as TriviaMatchState;
    const turnPid = st0.playerIds[st0.currentPlayerIndex] ?? st0.playerIds[0];
    if (turnPid !== uid || m.status !== "active") {
      throw new Error(turnPid !== uid ? "Not your turn" : "Match finished");
    }
    if (st0.phase !== "answer" || !st0.activeQuestionId) {
      throw new Error("No active question");
    }
    const eliminated = st0.answerRound?.eliminatedChoiceIndexes ?? [];
    if (eliminated.includes(choiceIndex)) {
      throw new Error("Choice eliminated by 50/50");
    }
    const recentTail = m.clientMoveIds.slice(-40);
    if (recentTail.includes(clientMoveId)) {
      let pub = sanitizeTriviaState(uid, m.stateJson, m.playersJson);
      pub = enrichWithQuestion(pub);
      return {
        idempotent: true,
        version: m.version,
        state: pub,
      } as T;
    }
    const qrow = blob.triviaQuestions.find((x) => x.id === st0.activeQuestionId);
    if (!qrow || !isTriviaCategoryKey(qrow.categoryKey)) {
      throw new Error("Bad question");
    }
    const correct = choiceIndex === qrow.correctIndex;
    const wedgesBefore = new Set(st0.wedges[uid] ?? []);
    const next = applyAnswer({
      state: st0,
      playerId: uid,
      correct,
      category: qrow.categoryKey,
    });
    const wedgesAfter = new Set(next.wedges[uid] ?? []);
    if (correct) {
      const syn = synergyBonusPointsForAnswer(
        m.eventSnapshotJson,
        wedgesBefore,
        wedgesAfter,
      );
      if (syn > 0) {
        next.scores[uid] = (next.scores[uid] ?? 0) + syn;
      }
    }
    let newWedge = false;
    for (const w of wedgesAfter) {
      if (!wedgesBefore.has(w)) newWedge = true;
    }
    const won = playerHasAllWedges(next.wedges, uid);
    const nextStatus = won ? "finished" : "active";
    m.version += 1;
    m.stateJson = next;
    m.status = nextStatus;
    m.updatedAt = Date.now();
    m.clientMoveIds.push(clientMoveId);
    if (m.clientMoveIds.length > 120) {
      m.clientMoveIds.splice(0, m.clientMoveIds.length - 40);
    }
    bumpQuestProgress(uid, {
      playedTurn: true,
      correctAnswer: correct,
      newWedge,
    });
    awardXp(uid, 15);
    resolveCpuTurns(m, id);
    if (m.status === "finished" && !m.finishEconomyApplied) {
      applyOfflineDuelFinishEconomyOnce(id, m);
    } else if (m.status === "active") {
      persistOfflineEconomy();
    }
    persistOfflineMatches();
    let pub = sanitizeTriviaState(uid, m.stateJson, m.playersJson);
    pub = enrichWithQuestion(pub);
    const rivalForWinner = m.playersJson.find((p) => p !== uid) ?? "";
    let winnerId: string | undefined;
    if (m.status === "finished" && rivalForWinner) {
      const w = m.stateJson.wedges;
      if (playerHasAllWedges(w, uid) && !playerHasAllWedges(w, rivalForWinner)) {
        winnerId = uid;
      } else if (
        playerHasAllWedges(w, rivalForWinner) &&
        !playerHasAllWedges(w, uid)
      ) {
        winnerId = rivalForWinner;
      }
    }
    return {
      version: m.version,
      correct,
      status: m.status,
      winnerId,
      state: pub,
    } as T;
  }

  const claim2xP = /^\/matches\/([^/]+)\/claim-results-2x$/.exec(cleanPath);
  if (claim2xP && method === "POST") {
    const id = decodeURIComponent(claim2xP[1]);
    const m = blob.matches.get(id);
    if (!m || !m.playersJson.includes(uid) || m.status !== "finished") {
      throw new Error("Match not found");
    }
    if (m.results2xClaimedByUserId?.[uid]) {
      return { ok: true, alreadyClaimed: true } as T;
    }
    let snap = m.resultsPayoutByUserId?.[uid];
    if (!snap && m.finishEconomyApplied) {
      const rivalId = m.playersJson.find((p) => p !== uid);
      if (!rivalId) throw new Error("Bad roster");
      const st = m.stateJson;
      const outcome = triviaDuelOutcomeForViewer({
        wedges: st.wedges,
        scores: st.scores,
        myId: uid,
        rivalId,
      });
      const myScore = st.scores[uid] ?? 0;
      snap = {
        coins: duelResultsCoinsForOutcome(outcome, myScore),
        xp: duelResultsXpForOutcome(outcome, myScore),
      };
    }
    if (!snap) throw new Error("No results payout");
    blob.currencies[uid] ??= {
      coins: 0,
      gems: 0,
      duelPowerBomb: 5,
      duelPowerTime: 5,
      duelPowerSkip: 10,
    };
    blob.currencies[uid].coins += snap.coins;
    awardXp(uid, snap.xp);
    m.results2xClaimedByUserId = {
      ...(m.results2xClaimedByUserId ?? {}),
      [uid]: true,
    };
    persistOfflineEconomy();
    persistOfflineMatches();
    return { ok: true, alreadyClaimed: false } as T;
  }

  const reactP = /^\/matches\/([^/]+)\/reactions$/.exec(cleanPath);
  if (reactP && method === "POST") {
    const id = decodeURIComponent(reactP[1]);
    const m = blob.matches.get(id);
    if (!m || !m.playersJson.includes(uid)) throw new Error("Match not found");
    return { ok: true } as T;
  }

  const rematchP = /^\/matches\/([^/]+)\/rematch$/.exec(cleanPath);
  if (rematchP && method === "POST") {
    const prevId = decodeURIComponent(rematchP[1]);
    const prev = blob.matches.get(prevId);
    if (!prev || !prev.playersJson.includes(uid)) {
      throw new Error("Match not found");
    }
    ensureInit();
    if (blob.triviaQuestions.length < 6) throw new Error("Catalog too small");
    const players = [...prev.playersJson].sort();
    const eventSnap =
      prev.eventSnapshotJson ??
      buildLiveEventSnapshotPayload(
        offlineLiveEventsList(Date.now()).map((e) => ({
          key: e.key,
          rules: e.rules,
        })),
      );
    const st = createInitialTriviaState(players, crypto.randomUUID(), {
      firstTurnPlayerId: uid,
    });
    const mid = crypto.randomUUID();
    blob.matches.set(mid, {
      playersJson: players,
      stateJson: st,
      version: 0,
      status: "active",
      eventSnapshotJson: eventSnap,
      updatedAt: Date.now(),
      clientMoveIds: [],
    });
    persistOfflineMatches();
    return { matchId: mid } as T;
  }

  throw new Error(`Offline: unsupported ${method} ${cleanPath}`);
}
